import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { Spacer } from 'jbc-front/components/presenters/Spacer';
import { Label } from 'jbc-front/components/presenters/form/Label';
import { Textarea } from 'jbc-front/components/presenters/form/Textarea';
import { Radio, RadioContainer } from 'jbc-front/components/presenters/form/Radio';
import { ErrorMessage } from 'jbc-front/components/presenters/form/ErrorMessage';
import { AnchorTarget } from '@/components/AnchorTarget';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import type { Schema } from '../../schema';

export const Hearing: FC = () => {
  return (
    <Panel>
      <Headding>聴力</Headding>
      <Item>
        <FrequencyCategory frequency="1000Hz" />
        <FrequencyCategory frequency="4000Hz" />
        <FrequencyCategory frequency="Other" />
      </Item>
    </Panel>
  );
};

interface FrequencyCategoryProps {
  className?: string;
  frequency: '1000Hz' | '4000Hz' | 'Other';
}

const categoryHeaders = {
  '1000Hz': '1,000Hz',
  '4000Hz': '4,000Hz',
  Other: 'その他の方法'
};

const FrequencyCategory: FC<FrequencyCategoryProps> = ({ frequency }) => {
  const {
    register,
    formState: { errors }
  } = useFormContext<Schema>();
  const idPrefix = `hearing_${frequency.toLowerCase()}`;
  const categoryErrors = errors.items?.[`hearing${frequency}`];
  const leftErrors = errors.items?.[`hearing${frequency}Left`];
  const rightErrors = errors.items?.[`hearing${frequency}Right`];

  return (
    <Panel>
      <AnchorTarget name={idPrefix} />
      <Headding>{categoryHeaders[frequency]}</Headding>
      <Item>
        <div>
          <Label>実施有無</Label>
          <Spacer size={8} />
          <RadioContainer>
            <Radio {...register(`items.hearing${frequency}.checked`)} value="true">
              実施
            </Radio>
            <Radio {...register(`items.hearing${frequency}.checked`)} value="false">
              未実施
            </Radio>
          </RadioContainer>
          {categoryErrors?.checked?.message && <ErrorMessage>{categoryErrors.checked.message}</ErrorMessage>}
        </div>
        <div className="u-mt20">
          <Label>所見有無</Label>
          <Spacer size={8} />
          <RadioContainer>
            <Radio {...register(`items.hearing${frequency}.hasFinding`)} value="false">
              所見なし
            </Radio>
            <Radio {...register(`items.hearing${frequency}.hasFinding`)} value="true">
              所見あり
            </Radio>
          </RadioContainer>
          {categoryErrors?.hasFinding?.message && <ErrorMessage>{categoryErrors.hasFinding.message}</ErrorMessage>}
        </div>
        <Panel className="u-mt20">
          <AnchorTarget name={`${idPrefix}_left`} />
          <Headding>左耳</Headding>
          <Item>
            <div>
              <Label>結果</Label>
              <Spacer size={8} />
              <Textarea {...register(`items.hearing${frequency}Left.value`)} rows={3} isError={!!leftErrors?.value} />
              {leftErrors?.value?.message && <ErrorMessage>{leftErrors.value.message}</ErrorMessage>}
            </div>
            <div className="u-mt20">
              <Label>評価</Label>
              <Spacer size={8} />
              <Textarea
                {...register(`items.hearing${frequency}Left.evaluation`)}
                rows={3}
                isError={!!leftErrors?.evaluation}
              />
              {leftErrors?.evaluation?.message && <ErrorMessage>{leftErrors.evaluation.message}</ErrorMessage>}
            </div>
          </Item>
        </Panel>
        <Panel className="u-mb0">
          <AnchorTarget name={`${idPrefix}_right`} />
          <Headding>右耳</Headding>
          <Item>
            <div>
              <Label>結果</Label>
              <Spacer size={8} />
              <Textarea {...register(`items.hearing${frequency}Right.value`)} rows={3} isError={!!rightErrors?.value} />
              {rightErrors?.value?.message && <ErrorMessage>{rightErrors.value.message}</ErrorMessage>}
            </div>
            <div className="u-mt20">
              <Label>評価</Label>
              <Spacer size={8} />
              <Textarea
                {...register(`items.hearing${frequency}Right.evaluation`)}
                rows={3}
                isError={!!rightErrors?.evaluation}
              />
              {rightErrors?.evaluation?.message && <ErrorMessage>{rightErrors.evaluation.message}</ErrorMessage>}
            </div>
          </Item>
        </Panel>
      </Item>
    </Panel>
  );
};
